import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import GroupBody from './GroupBody';
import SituationBody from './SituationBody';
import ChoicesBody from './ChoicesBody';
import UserSubmittedBody from './UserSubmittedBody';
import FreeResponseBody from './FreeResponseBody';
import styles from './styles/ViewSituation.module.css';
import { useNavigate } from 'react-router-dom';
import { useScenario } from '../../../context/ScenarioContext';
import FeedbackModal from './FeedbackModal';
import { FaCommentDots } from 'react-icons/fa';
import { Tooltip } from 'react-tooltip';
import ViewCanvas from './ViewCanvas';
import { FaExpand, FaCompress } from 'react-icons/fa';
import Loading from '../../loading/Loading';

const ViewSituation = ({
    userId,
    scenario,
    canvas,
    currentSituationId,
    setCurrentSituationId,
    preview,
    userLoading,
    userKeys,
    setUserKeys
}) => {
    const [currentSituation, setCurrentSituationInternal] = useState(null);
    const [isLoadingSituation, setIsLoadingSituation] = useState(false);
    const navigate = useNavigate();
    const { addDecisionToLog, completeLog } = useScenario();
    const [feedbackText, setFeedbackText] = useState('');
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    const [isMinimapExpanded, setIsMinimapExpanded] = useState(false);

    useEffect(() => {
        const fetchCurrentSituation = async () => {
            if (!currentSituationId || !scenario?._id || userLoading) {
                if (!userLoading && !currentSituationId) {
                    setCurrentSituationInternal(null);
                }
                return;
            }

            setIsLoadingSituation(true);
            try {
                const situationResponse = await axios.get(
                    `${process.env.REACT_APP_API_URL_LOCAL}/api/situations/situation/${currentSituationId}`
                );
                const situationData = situationResponse.data;
                setCurrentSituationInternal(situationData);

                if (situationData.grantsKeys && situationData.grantsKeys.length > 0) {
                    const currentGlobalUserKeys = userKeys;
                    const newKeyIdsToGrant = situationData.grantsKeys.filter(
                        keyId => !currentGlobalUserKeys.includes(keyId)
                    );

                    if (newKeyIdsToGrant.length > 0) {
                        if (userId) {
                            await axios.patch(
                                `${process.env.REACT_APP_API_URL_LOCAL}/api/users/${userId}/grantKeys`,
                                { keys: newKeyIdsToGrant },
                                { withCredentials: true }
                            );
                            setUserKeys(prevKeys => [...new Set([...prevKeys, ...newKeyIdsToGrant])]);
                        } else {
                            const storedKeyObjectsRaw = sessionStorage.getItem('userKeys');
                            let storedKeyObjects = storedKeyObjectsRaw ? JSON.parse(storedKeyObjectsRaw) : [];
                            const newKeyObjectsToAdd = newKeyIdsToGrant.map(keyId => ({
                                keyId,
                                scenarioId: scenario._id
                            }));
                            const updatedKeyObjects = [...storedKeyObjects, ...newKeyObjectsToAdd];
                            sessionStorage.setItem('userKeys', JSON.stringify(updatedKeyObjects));
                            setUserKeys(updatedKeyObjects.map(ko => ko.keyId));
                        }
                    }
                }

                if (scenario.isLogged && situationData.isLogged && !preview) {
                    await addDecisionToLog({
                        type: 'situation',
                        id: situationData._id,
                        title: situationData.title
                    });
                }
            } catch (error) {
                console.error(`Error fetching situation: ${error}`);
                setCurrentSituationInternal(null);
            } finally {
                setIsLoadingSituation(false);
            }
        };

        fetchCurrentSituation();
    }, [
        currentSituationId,
        scenario?._id,
        scenario?.isLogged,
        userId,
        setUserKeys,
        addDecisionToLog,
        preview,
        userLoading
    ]);
    

    const handleFinish = useCallback(async () => {
        if (!scenario?._id) return;

        if (scenario.isLogged && !preview) {
            await completeLog();
        }
        
        try {
            if (!preview) {
                await axios.patch(
                    `${process.env.REACT_APP_API_URL_LOCAL}/api/scenarios/${scenario._id}/completions`,
                     {}, { withCredentials: true }
                );
            }

            if (userId) {
                await axios.patch(
                    `${process.env.REACT_APP_API_URL_LOCAL}/api/users/${userId}/removeKeys`,
                    { scenarioId: scenario._id }, { withCredentials: true }
                );
                const userResponse = await axios.get(
                    `${process.env.REACT_APP_API_URL_LOCAL}/api/users/${userId}`, { withCredentials: true }
                );
                setUserKeys(userResponse.data.keys || []);
            } else {
                const storedKeyObjectsRaw = sessionStorage.getItem('userKeys');
                const storedKeyObjects = storedKeyObjectsRaw ? JSON.parse(storedKeyObjectsRaw) : [];
                const updatedKeyObjects = storedKeyObjects.filter(keyObj => keyObj.scenarioId !== scenario._id);
                sessionStorage.setItem('userKeys', JSON.stringify(updatedKeyObjects));
                setUserKeys(updatedKeyObjects.map(ko => ko.keyId));
            }
        } catch (error) {
            console.error(`Error during finish process: ${error}`);
        } finally {
            setCurrentSituationId(null);
            navigate('/');
        }
    }, [scenario, userId, setUserKeys, setCurrentSituationId, navigate, completeLog, preview]);

    const closeFeedbackModal = useCallback(() => {
        setShowFeedbackModal(false);
        setFeedbackText('');
    }, []);

    const handleToggleMinimap = useCallback(() => {
        setIsMinimapExpanded(prev => !prev);
    }, []);

    const renderConditionalBody = () => {
        if (!currentSituation) return null;

        if (currentSituation.isUserSubmitted) {
            return <UserSubmittedBody situation={currentSituation} setCurrentSituationId={setCurrentSituationId} isScenarioLogged={scenario?.isLogged} preview={preview} />;
        } else if (currentSituation.isFreeResponse) {
            return <FreeResponseBody situation={currentSituation} setCurrentSituationId={setCurrentSituationId} isScenarioLogged={scenario?.isLogged} preview={preview} />;
        } else if (currentSituation.choices && currentSituation.choices.length > 0) {
            return (
                <ChoicesBody
                    choices={currentSituation.choices}
                    setCurrentSituationId={setCurrentSituationId}
                    isScenarioLogged={scenario?.isLogged}
                    preview={preview}
                    scenario={scenario} 
                    userKeys={userKeys}
                    setUserKeys={setUserKeys}
                    userId={userId}
                />
            );
        } else if (currentSituation.isEnd && !preview) {
            return <button className={styles.button} onClick={handleFinish}>Home</button>;
        }
        return null;
    };

    if (userLoading) {
        return <div className={styles.container}><Loading /></div>;
    }
    if (isLoadingSituation) {
        return <div className={styles.container}><Loading /></div>;
    }
    
    if (!currentSituationId || !currentSituation) {
        if (currentSituationId && !isLoadingSituation && !currentSituation) {
             return <div className={styles.loadingContainer}>Error loading data.</div>;
        }
        return <div className={styles.container}><Loading /></div>;
    }

    return (
        <div className={`${styles.container} ${preview ? styles.previewPadding : ''}`}>
            {currentSituation?.situationGroup && (
                <GroupBody group={currentSituation.situationGroup} />
            )}
    
            <SituationBody situation={currentSituation} />
            {renderConditionalBody()}
            
            {(scenario?.showMap || preview) && canvas && (
                <div className={isMinimapExpanded ? styles.canvasExpanded : styles.canvasMinimap}>
                    <ViewCanvas canvas={canvas} currentSituationId={currentSituationId} isMinimapExpanded={isMinimapExpanded} />
                    <button type="button" aria-label={isMinimapExpanded ? "Compress minimap" : "Expand minimap"} className={styles.toggleButton} onClick={handleToggleMinimap}>
                        {isMinimapExpanded ? <FaCompress /> : <FaExpand />}
                    </button>
                </div>
            )}

            {showFeedbackModal && (
                <FeedbackModal
                    userId={userId}
                    closeFeedbackModal={closeFeedbackModal}
                    feedbackText={feedbackText}
                    setFeedbackText={setFeedbackText}
                    scenario={scenario}
                    situation={currentSituation}
                />
            )}

            {!preview && (
                <FaCommentDots 
                    className={styles.feedbackButton}
                    onClick={() => setShowFeedbackModal(true)}
                    data-tooltip-id="vs-tooltip"
                    data-tooltip-content="Submit Feedback"
                />
            )}
            
            <Tooltip id="vs-tooltip" place="left" style={{ zIndex: 9999 }} />
        </div>
    );
};

export default ViewSituation;